<template>
  <div class="choices-combinations-container">
    <div
      v-for="(combination, index) in choicesCombinations"
      :key="index"
      class="q-mb-lg flex row items-baseline"
    >
      <div class="choices-combinations-input-container ">
        <choices-combinations-input
          :index="index"
          :combination="combination"
          :show-tooltip="true"
          :add-choices="addChoices"
          :remove-choice="removeChoice"
          :update-invalid-value="updateInvalidCombinationValue"
          :invalid-choices="invalidChoices"
          :choices-combinations-input-rules="choicesCombinationsRules"
          :product-model="productModel"
          :local-storage-preconditions="combination.preCondition"
          :show-expression-on-focus="showExpressionOnFocusFor(index)"
          :disable="disablePreconditionField"
          @focus="onInputFocus(index)"
          @blur="onInputBlur(index)"
        >
        </choices-combinations-input>
      </div>

      <mi-btn
        v-if="!(index === 0 && choicesCombinations.length === 1)"
        class="q-ml-xs q-mt-xs q-pa-none"
        icon="minus-circle"
        icon-size="18px"
        icon-type
        fab
        flat
        @click="removeCombination(index)"
      ></mi-btn>
    </div>
    <div>
      <mi-btn
        v-show="true"
        :disable="disableAddBtn || disablePreconditionField"
        icon="plus"
        icon-type
        icon-size="12px"
        outline
        @click="addCombination"
      >
        Add another combination
      </mi-btn>
    </div>
  </div>
</template>

<script>
  import { ref, watch, onMounted, computed } from 'vue'
  import { required } from '@/utils/validators'
  import ChoicesCombinationsInput from './ChoicesCombinationsInput.vue'

  export default {
    name: 'ChoicesCombinationsContainer',
    components: { ChoicesCombinationsInput },
    props: {
      invalidChoices: {
        type: Set,
        required: true
      },
      formCleared: {
        type: Number,
        default: 0
      },
      productModel: {
        type: Object,
        required: false,
        default: () => {}
      },
      showExpressionOnFocus: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    emits: ['update:pre-conditions'],
    setup(props, { emit }) {
      const choicesCombinations = ref([{ preCondition: [], message: '', invalid: false }])
      const maxCombinations = ref(5)
      const disableAddBtn = ref(false)
      const choicesCombinationsRules = ref([required])

      const disablePreconditionField = computed(() => props.productModel === '')

      const loadPreconditions = () => {
        if (props.formCleared > 0) {
          return
        }

        try {
          const savedData = localStorage.getItem('inspector-preconditions')
          if (savedData) {
            const parsedData = JSON.parse(savedData)
            if (Array.isArray(parsedData) && parsedData.length > 0) {
              choicesCombinations.value = parsedData.map(item => ({
                preCondition: item.preCondition || [],
                message: '',
                invalid: false
              }))

              disableAddBtn.value = choicesCombinations.value.length >= maxCombinations.value
            }
          }
        }
        catch (error) {
          console.error('Error loading preconditions from localStorage:', error)
          localStorage.removeItem('inspector-preconditions')
        }
      }

      const focusedIndex = ref(null)
      const lastActiveIndex = ref(null)

      const onInputFocus = index => {
        focusedIndex.value = index
        lastActiveIndex.value = index
      }

      const onInputBlur = index => {
        if (focusedIndex.value === index) focusedIndex.value = null
      }

      const showExpressionOnFocusFor = index => {
        if (choicesCombinations.value.length <= 1) return false

        if (focusedIndex.value !== null) return true

        return index !== lastActiveIndex.value
      }

      const savePreconditions = () => {
        try {
          const dataToSave = choicesCombinations.value.map(item => ({
            preCondition: item.preCondition
          }))
          localStorage.setItem('inspector-preconditions', JSON.stringify(dataToSave))
        }
        catch (error) {
          console.error('Error saving preconditions to localStorage:', error)
        }
      }

      const addChoices = (choices, combinationIndex) => {
        choicesCombinations.value[combinationIndex].preCondition = choices
        savePreconditions()
        emit('update:pre-conditions', { choicesCombinations: choicesCombinations.value })
      }

      const removeChoice = (choice, combinationIndex) => {
        choicesCombinations.value[combinationIndex].preCondition.splice(choice.index, 1)
      }

      const addCombination = () => {
        if (choicesCombinations.value.length >= maxCombinations.value) return
        choicesCombinations.value.push({ preCondition: [], message: '', invalid: false })
        if (choicesCombinations.value.length === maxCombinations.value) {
          disableAddBtn.value = true
        }
        savePreconditions()
      }

      const removeCombination = index => {
        choicesCombinations.value.splice(index, 1)

        if (focusedIndex.value === index) focusedIndex.value = null

        if (lastActiveIndex.value === index) {
          lastActiveIndex.value = Math.max(0, index - 1)
        }
        else if (lastActiveIndex.value > index) {
          lastActiveIndex.value -= 1
        }

        if (choicesCombinations.value.length < maxCombinations.value) {
          disableAddBtn.value = false
        }
        savePreconditions()
      }

      const updateInvalidCombinationValue = (index, field, value) => {
        choicesCombinations.value[index][field] = value
      }

      const validateInvalidChoices = () => {
        choicesCombinations.value.map(setting => {
          const hasMatch = setting.preCondition?.some(item => props.invalidChoices.has(item))
          if (hasMatch) {
            setting.invalid = true
            setting.message = 'Non existing code(s) in product model. Enter only valid codes.'
          }
          return setting
        })
      }

      watch(
        () => [choicesCombinations.value, props.invalidChoices],
        () => {
          validateInvalidChoices()
          emit('update:pre-conditions', { choicesCombinations: choicesCombinations.value })
        },
        { deep: true }
      )

      watch(() => props.formCleared, (newVal, oldVal) => {
        if (newVal !== oldVal) {
          choicesCombinations.value = [{ preCondition: [], message: '', invalid: false }]
          disableAddBtn.value = false

          const currentData = localStorage.getItem('inspector-preconditions')

          localStorage.removeItem('inspector-preconditions')

          setTimeout(() => {
            if (currentData) {
              localStorage.setItem('inspector-preconditions', currentData)
            }
          }, 1000)

          emit('update:pre-conditions', { choicesCombinations: choicesCombinations.value })
        }
      })

      onMounted(() => {
        loadPreconditions()
      })

      return {
        choicesCombinations,
        updateInvalidCombinationValue,
        removeCombination,
        addCombination,
        removeChoice,
        addChoices,
        disableAddBtn,
        disablePreconditionField,
        choicesCombinationsRules,
        showExpressionOnFocusFor,
        onInputFocus,
        onInputBlur
      }
    }
  }
</script>

<style lang="scss" scoped>
  // stylelint-disable declaration-no-important
  .choices-combinations-input-container {
    display: flex;
    flex-direction: column;
    width: 90%;
  }

  .choices-combinations-container {
    background-color: $mi-white;
    width: 564px;
  }

  .q-btn {
    font-size: 14px;
  }

  .mi-btn.q-btn.disabled:hover {
    color: #d3d3d3 !important;
  }

  ::v-deep(.logic-expression-container) {
    margin-top: 4px !important;
    margin-bottom: 0 !important;
  }

  ::v-deep(.q-field) {
    margin-left: 0 !important;
  }

</style>
